import { useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import apiClient from "@/api/ApiClient";
import { useTranslation } from "@/hooks/useTranslation";

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";

// Initialize Stripe
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

interface StripeCheckoutProps {
  packageId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  platform?: string;
}

/**
 * Component that redirects to Stripe Checkout
 * For token purchases, we use Stripe Checkout hosted page (redirect flow)
 */
export const StripeCheckout = ({ 
  packageId, 
  onSuccess, 
  onCancel,
  platform = "web"
}: StripeCheckoutProps) => {
  useEffect(() => {
    const initiateCheckout = async () => {
      try {
        // Create checkout session
        const successUrl = window.location.origin + "/payment/success?session_id={CHECKOUT_SESSION_ID}";
        const cancelUrl = window.location.origin + "/payment/cancel";
        
        const response = await apiClient.createCheckoutSession(
          packageId,
          platform,
          successUrl,
          cancelUrl
        );

        if (response.success && response.url) {
          // Redirect to Stripe Checkout
          window.location.href = response.url;
        } else {
          console.error("Failed to create checkout session:", response.message);
          if (onCancel) onCancel();
        }
      } catch (error: any) {
        console.error("Error creating checkout session:", error);
        if (onCancel) onCancel();
      }
    };

    initiateCheckout();
  }, [packageId, platform, onSuccess, onCancel]);

  return (
    <div className="flex items-center justify-center p-8">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">{t('tokens.redirecting_checkout')}</p>
      </div>
    </div>
  );
};

/**
 * Stripe Elements wrapper for future use (if we want embedded checkout)
 */
export const StripeElementsWrapper = ({ children }: { children: React.ReactNode }) => {
  if (!stripePromise) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY.
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      {children}
    </Elements>
  );
};

